import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Buildings, Lock, ArrowRight, CheckCircle } from '@phosphor-icons/react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Die Passwörter stimmen nicht überein');
      return;
    }

    if (newPassword.length < 6) {
      setError('Das Passwort muss mindestens 6 Zeichen haben');
      return;
    }

    setLoading(true);
    
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, { 
        token, 
        new_password: newPassword 
      });
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        setError(detail);
      } else {
        setError('Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-8" data-testid="reset-password-page">
        <div className="w-full max-w-md">
          <div className="bg-white border border-gray-200 p-8 text-center">
            <div className="flex items-center gap-3 justify-center mb-8">
              <Buildings weight="bold" className="w-8 h-8 text-slate-900" />
              <span className="font-heading text-xl font-black tracking-tight">IMMO VERWALTER</span>
            </div>
            <h1 className="font-heading text-2xl font-black tracking-tight text-slate-900 mb-4">
              Ungültiger Link
            </h1>
            <p className="text-slate-500 mb-6">
              Der Reset-Link ist ungültig oder fehlt.
            </p>
            <Link 
              to="/forgot-password"
              className="text-slate-900 font-medium hover:underline"
            >
              Neuen Link anfordern
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-8" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="bg-white border border-gray-200 p-8">
          <div className="flex items-center gap-3 mb-8">
            <Buildings weight="bold" className="w-8 h-8 text-slate-900" />
            <span className="font-heading text-xl font-black tracking-tight">IMMO VERWALTER</span>
          </div>

          {success ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle weight="fill" className="w-10 h-10 text-emerald-600" />
              </div>
              <h1 className="font-heading text-2xl font-black tracking-tight text-slate-900 mb-2">
                Passwort geändert!
              </h1>
              <p className="text-slate-500 mb-4">
                Sie werden zur Anmeldung weitergeleitet...
              </p>
            </div>
          ) : (
            <>
              <h1 className="font-heading text-2xl font-black tracking-tight text-slate-900 mb-2">
                Neues Passwort setzen
              </h1>
              <p className="text-slate-500 text-sm mb-6">
                Geben Sie Ihr neues Passwort ein.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 text-sm" data-testid="reset-password-error">
                    {error}
                  </div>
                )}

                <div>
                  <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">
                    Neues Passwort
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full border-b border-slate-300 bg-transparent pl-8 pr-0 py-3 focus:border-slate-900 focus:ring-0 outline-none transition-colors text-slate-900"
                      placeholder="••••••••"
                      required
                      minLength={6}
                      data-testid="new-password-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">
                    Passwort bestätigen
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full border-b border-slate-300 bg-transparent pl-8 pr-0 py-3 focus:border-slate-900 focus:ring-0 outline-none transition-colors text-slate-900"
                      placeholder="••••••••"
                      required
                      minLength={6}
                      data-testid="confirm-password-input"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-slate-900 text-white py-3 px-6 font-medium hover:bg-slate-800 focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="reset-password-submit-button"
                >
                  {loading ? 'Wird gespeichert...' : 'Passwort speichern'}
                  <ArrowRight weight="bold" className="w-4 h-4" />
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
